import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { SVGFC } from '@repo/toolkit/types'
import { PropsWithChildren, useState } from 'react'

import { CheckMarkFourIcon, OpenedLockTwoIcon, PdfIcon, XThreeIcon } from '../../icons'
import ClosedLockTwoIcon from '../../icons/closed-lock-02'
import Badge from './badge'
import Typography from './typography'

type DocumentInformationStatus = 'pending' | 'validated' | 'to-be-validated'

type DocumentInformationProps = {
  icon: SVGFC
  title: string
  status: DocumentInformationStatus
  locked: boolean
  onLockChange?: (locked: boolean) => Promise<void>
}

const DocumentInformation = ({
  title,
  icon: Icon,
  locked,
  status,
  children,
  onLockChange
}: PropsWithChildren<DocumentInformationProps>) => {
  const [lock, setLock] = useState(locked)
  const [loading, setLoading] = useState(false)

  return (
    <div className="rounded-lg bg-white p-3 flex flex-col gap-4 border border-grey-light">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            <Icon className="w-7 h-7" color={tailwindColors.grey.DEFAULT} />
            <Typography variant="tag" color="grey">
              {title}
            </Typography>
          </div>

          <DocumentInformation.Status status={status} />
        </div>

        <div
          className="flex items-center justify-center p-1 border border-grey-light rounded cursor-pointer"
          onClick={async () => {
            if (loading) return
            const newLockState = !lock
            setLock(newLockState)
            setLoading(true)
            await onLockChange?.(newLockState)
            setLoading(false)
          }}
        >
          {lock ? (
            <ClosedLockTwoIcon className="w-6 h-6" color={tailwindColors.green.DEFAULT} />
          ) : (
            <OpenedLockTwoIcon className="w-6 h-6" color={tailwindColors.red.DEFAULT} />
          )}
        </div>
      </div>

      {children}
    </div>
  )
}

export default DocumentInformation

type DocumentInformationStatusProps = {
  status: DocumentInformationStatus
}

DocumentInformation.Status = ({ status }: DocumentInformationStatusProps) => {
  if (status === 'pending')
    return (
      <Badge
        variant="danger"
        text="En attente"
        iconPosition="start"
        icon={<XThreeIcon width="22px" height="22px" color={tailwindColors.red.DEFAULT} />}
      />
    )

  if (status === 'to-be-validated')
    return (
      <Badge
        variant="warning"
        text="À valider"
        iconPosition="start"
        icon={
          <CheckMarkFourIcon width="22px" height="22px" color={tailwindColors.orange.DEFAULT} />
        }
      />
    )

  return (
    <Badge
      variant="success"
      text="Terminé"
      iconPosition="start"
      icon={<CheckMarkFourIcon width="22px" height="22px" color={tailwindColors.green.DEFAULT} />}
    />
  )
}

type DocumentInformationPdfDocument = {
  text?: string
  status: DocumentInformationStatus
  onClick?: () => void
}

DocumentInformation.PdfDocument = ({
  text,
  status,
  onClick,
  children
}: PropsWithChildren<DocumentInformationPdfDocument>) => {
  return (
    <div className="rounded border border-grey-light bg-white-hover p-1 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 flex-row">
        <div className="w-7 h-7 bg-white-dark rounded-sm flex items-center justify-center">
          <PdfIcon
            className="w-5 h-5"
            color={
              status === 'pending' ? tailwindColors.grey.DEFAULT : tailwindColors.blue.DEFAULT
            }
          />
        </div>
        <Typography.Link variant="label-medium" onClick={onClick}>
          {text}
        </Typography.Link>
      </div>

      {children}
    </div>
  )
}
